import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import baseURL from './Components/Apiurs/Apiurls';

const AddProductForm = () => {
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [productDetails, setProductDetails] = useState({
    main_category: '',
    sub_category: '',
    selected_product: '', // Store only product_id
    size: '',
    listprice: '',
    moq: '',
    hsncode: '',
    batch: ''
  });

  const [addedProducts, setAddedProducts] = useState([]);

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);

  const [newMainCategory, setNewMainCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [newProduct, setNewProduct] = useState('');

  // Fetch main categories
  useEffect(() => {
    axios.get(`${baseURL}/api/main-categories`)
      .then(response => setMainCategories(response.data))
      .catch(error => console.error("Error fetching main categories", error));
  }, []);

  // Fetch subcategories based on selected main category
  useEffect(() => {
    if (productDetails.main_category) {
      axios.get(`${baseURL}/api/sub-categories/${productDetails.main_category}`)
        .then(response => setSubCategories(response.data))
        .catch(error => console.error("Error fetching subcategories", error));
    }
  }, [productDetails.main_category]);

  // Fetch products based on selected subcategory
  useEffect(() => {
    if (productDetails.sub_category) {
      axios.get(`${baseURL}/api/products/${productDetails.sub_category}`)
        .then(response => setProducts(response.data))
        .catch(error => console.error("Error fetching products", error));
    }
  }, [productDetails.sub_category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductDetails(prevDetails => ({
      ...prevDetails,
      [name]: value
    }));
  };

  // Handle category, subcategory, and product creation
  const handleCategorySubmit = (e) => {
    e.preventDefault();
    axios.post(`${baseURL}/api/main-categories`, { maincategory_name: newMainCategory })
      .then(response => {
        setMainCategories([...mainCategories, response.data]);
        setNewMainCategory('');
        setShowCategoryModal(false);
      })
      .catch(error => console.error("Error adding main category", error));
  };

  const handleSubCategorySubmit = (e) => {
    e.preventDefault();
    axios.post(`${baseURL}/api/sub-categories`, {
      subcategory_name: newSubCategory,
      maincategory_id: productDetails.main_category
    })
      .then(response => {
        setSubCategories([...subCategories, response.data]);
        setNewSubCategory('');
        setShowSubCategoryModal(false);
      })
      .catch(error => console.error("Error adding subcategory", error));
  };

  const handleProductNameSubmit = (e) => {
    e.preventDefault();
    if (!productDetails.sub_category) {
      alert("Please select a subcategory before adding a product.");
      return;
    }
    axios.post(`${baseURL}/api/product-name`, {
      product_name: newProduct,
      subcategory_id: productDetails.sub_category
    })
      .then(response => {
        setProducts([...products, {
          product_id: response.data.product_id,
          product_name: response.data.product_name,
          subcategory_id: response.data.subcategory_id
        }]);
        setNewProduct('');
        setShowProductModal(false);
        alert("Product name added successfully");
      })
      .catch(error => console.error("Error adding product name", error));
  };

  // Add product batch
  const handleAddBatch = () => {
    const currentProduct = { ...productDetails };

    // Validate that all fields are filled
    if (!currentProduct.selected_product || !currentProduct.main_category || !currentProduct.sub_category) {
      alert("Please select a product, category, and subcategory.");
      return;
    }

    // Add the current product batch to the list
    const updatedProducts = [...addedProducts, currentProduct];
    setAddedProducts(updatedProducts);

    // Store it temporarily in localStorage
    localStorage.setItem('product_batches', JSON.stringify(updatedProducts));

    // Reset only the input fields, not the dropdowns (keep dropdowns unchanged)
    setProductDetails(prevDetails => ({
      ...prevDetails,
      size: '',
      listprice: '',
      moq: '',
      hsncode: '',
      batch: ''
    }));
  };

  // Handle submit
  const handleSubmit = () => {
    if (addedProducts.length === 0) {
      alert("Please add at least one product batch.");
      return;
    }

    // Send all the added products to the backend
    axios.post(`${baseURL}/api/products`, addedProducts)
      .then(response => {
        alert("Products added successfully.");
        // Clear the local storage and reset state
        localStorage.removeItem('product_batches');
        setAddedProducts([]);
      })
      .catch(error => {
        console.error("Error adding products", error);
        alert("Failed to add products: " + (error.response?.data?.error || error.message));
      });
  };

  // Remove a product batch from the added list
  const handleRemoveBatch = (index) => {
    const updatedList = addedProducts.filter((_, i) => i !== index);
    setAddedProducts(updatedList);
    localStorage.setItem('product_batches', JSON.stringify(updatedList));
  };

  return (
    <div className="container">
      <h2 className="my-4">Add Product</h2>
      <Form>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group controlId="mainCategory">
              <Form.Label>Main Category</Form.Label>
              <Form.Control as="select" name="main_category" value={productDetails.main_category} onChange={handleInputChange} required>
                <option value="">Select Main Category</option>
                {mainCategories.map(category => (
                  <option key={category.maincategory_id} value={category.maincategory_id}>
                    {category.maincategory_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button variant="link" onClick={() => setShowCategoryModal(true)}>+ Add</Button>
          </Col>

          <Col md={3}>
            <Form.Group controlId="subCategory">
              <Form.Label>Sub Category</Form.Label>
              <Form.Control as="select" name="sub_category" value={productDetails.sub_category} onChange={handleInputChange} required>
                <option value="">Select Sub Category</option>
                {subCategories.map(subCategory => (
                  <option key={subCategory.subcategory_id} value={subCategory.subcategory_id}>
                    {subCategory.subcategory_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button variant="link" onClick={() => setShowSubCategoryModal(true)}>+ Add</Button>
          </Col>

          <Col md={3}>
            <Form.Group controlId="productName">
              <Form.Label>Product</Form.Label>
              <Form.Control as="select" name="selected_product" value={productDetails.selected_product} onChange={handleInputChange} required>
                <option value="">Select Product</option>
                {products.map(product => (
                  <option key={product.product_id} value={product.product_id}>
                    {product.product_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button variant="link" onClick={() => setShowProductModal(true)}>+ Add</Button>
          </Col>
        </Row>

        {/* Input fields for size, hsncode, listprice, etc. */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="size">
              <Form.Label>Size</Form.Label>
              <Form.Control type="text" name="size" value={productDetails.size} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="hsncode">
              <Form.Label>HSN Code</Form.Label>
              <Form.Control type="text" name="hsncode" value={productDetails.hsncode} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="listprice">
              <Form.Label>List Price</Form.Label>
              <Form.Control type="number" name="listprice" value={productDetails.listprice} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="moq">
              <Form.Label>MOQ</Form.Label>
              <Form.Control type="number" name="moq" value={productDetails.moq} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="batch">
              <Form.Label>Batch</Form.Label>
              <Form.Control type="text" name="batch" value={productDetails.batch} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" onClick={handleAddBatch}>Add Batch</Button>
        <Button variant="primary" onClick={handleSubmit}>Submit All Products</Button>
      </Form>

      {/* Modal for Adding Category */}
      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Main Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCategorySubmit}>
            <Form.Group controlId="newMainCategory">
              <Form.Label>Category Name</Form.Label>
              <Form.Control type="text" value={newMainCategory} onChange={(e) => setNewMainCategory(e.target.value)} required />
            </Form.Group>
            <Button variant="primary" type="submit">Submit</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal for Adding Subcategory */}
      <Modal show={showSubCategoryModal} onHide={() => setShowSubCategoryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Sub Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubCategorySubmit}>
            <Form.Group controlId="newSubCategory">
              <Form.Label>Sub Category Name</Form.Label>
              <Form.Control type="text" value={newSubCategory} onChange={(e) => setNewSubCategory(e.target.value)} required />
            </Form.Group>
            <Button variant="primary" type="submit">Submit</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal for Adding Product */}
      <Modal show={showProductModal} onHide={() => setShowProductModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleProductNameSubmit}>
            <Form.Group controlId="newProduct">
              <Form.Label>Product Name</Form.Label>
              <Form.Control type="text" value={newProduct} onChange={(e) => setNewProduct(e.target.value)} required />
            </Form.Group>
            <Button variant="primary" type="submit">Submit</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Display Added Products */}
      <h4 className="my-4">Added Products</h4>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Product</th>
            <th>Model</th>
            <th>Size</th>
            <th>Price</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {addedProducts.map((product, index) => (
            <tr key={index}>
              <td>{product.selected_product}</td>
              <td>{product.batch}</td>
              <td>{product.size}</td>
              <td>{product.listprice}</td>
              <td>
                <Button variant="danger" onClick={() => handleRemoveBatch(index)}>Remove</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AddProductForm;
